import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, Store, Calculator, FileText, Users, BookOpen,
  LogOut, Menu, X, ChevronRight, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/vendors', label: 'Vendors', icon: Store },
  { to: '/calculator', label: 'Rate Calculator', icon: Calculator },
  { to: '/quotations', label: 'Quotations', icon: FileText },
  { to: '/customers', label: 'Customers', icon: Users },
  { to: '/catalog', label: 'Design Catalog', icon: BookOpen },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') setInstallPrompt(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1a365d] text-white shadow-lg" data-testid="app-header">
        <div className="flex items-center justify-between px-4 sm:px-6 h-14">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-btn"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img src="/logo.png" alt="Leela Solutions" className="w-8 h-8 rounded-lg object-contain bg-white" />
              <span className="font-['Outfit'] font-semibold text-lg tracking-tight hidden sm:inline">Leela Solutions</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50 hidden md:inline">Bazar Samiti, Chapra Saran 841301 | 6202371088</span>
            {installPrompt && (
              <Button
                size="sm"
                className="bg-[#ed8936] hover:bg-[#dd6b20] text-white text-xs h-7 px-3"
                onClick={handleInstall}
                data-testid="install-app-btn"
              >
                <Download size={13} className="mr-1" /> Install App
              </Button>
            )}
            <span className="text-sm text-white/70 hidden sm:inline">{user?.name || user?.email}</span>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-40 w-56 bg-slate-50 border-r border-slate-200 transform transition-transform duration-200 lg:transform-none ${
            sidebarOpen ? 'translate-x-0 top-14' : '-translate-x-full lg:translate-x-0'
          }`}
          data-testid="app-sidebar"
        >
          <nav className="flex flex-col gap-1 p-3 pt-4">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 group ${
                    isActive
                      ? 'bg-[#ed8936] text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
                data-testid={`nav-${to.slice(1)}`}
              >
                <Icon size={18} />
                <span>{label}</span>
                <ChevronRight size={14} className="ml-auto opacity-0 group-hover:opacity-50 transition-opacity" />
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 bg-white">
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
